import type { ComponentType } from 'react';
import {
  AlertCircle,
  CheckCircle,
  Clock,
  Mail01,
  MinusCircle,
  Pencil01,
  Send01,
  ShieldTick,
  UserQuestionmark,
  XCircle,
} from '@kula-ai/icons-core';
import consentErrors from '@/utils/errors/Modules/consent';
import type {
  CandidateConsentSummary,
  ConsentActionKey,
  DerivedConsentStatus,
} from './types';

export const EXPIRING_SOON_DAYS = 30;

// Expiry is time-derived on the current BE stack: a lapsed consent may still
// read `granted` with a past expires_at, so always compute it here.
export const deriveConsentStatus = (
  consent: Pick<CandidateConsentSummary, 'status' | 'expires_at'>
): DerivedConsentStatus => {
  const { status, expires_at } = consent;
  if (status === 'expired') return 'expired';
  if (status === 'granted' && expires_at) {
    const expiry = new Date(expires_at).getTime();
    if (expiry < Date.now()) return 'expired';
    const soonCutoff = Date.now() + EXPIRING_SOON_DAYS * 24 * 60 * 60 * 1000;
    if (expiry < soonCutoff) return 'expiring_soon';
  }
  return status;
};

type IconComponent = ComponentType<{ size?: number }>;

export interface ConsentTagConfig {
  label: string;
  variant: 'neutral' | 'warning';
  // Pastel overrides win over the variant surface (merged after presets)
  className?: string;
  Icon: IconComponent;
}

export const CONSENT_STATUS_TAG: Record<
  DerivedConsentStatus,
  ConsentTagConfig
> = {
  not_collected: {
    label: 'Consent not collected',
    variant: 'neutral',
    className: 'bg-secondary-50 text-secondary-700',
    Icon: MinusCircle,
  },
  requested: {
    label: 'Consent requested',
    variant: 'neutral',
    className: 'bg-pastel-3-light text-pastel-3-dark',
    Icon: Send01,
  },
  granted: {
    label: 'Consent granted',
    variant: 'neutral',
    className: 'bg-pastel-2-light text-pastel-2-dark',
    Icon: CheckCircle,
  },
  expiring_soon: {
    label: 'Consent expiring soon',
    variant: 'warning',
    className: 'bg-pastel-6-light text-pastel-6-dark',
    Icon: Clock,
  },
  expired: {
    label: 'Consent expired',
    variant: 'neutral',
    className: 'bg-pastel-1-light text-pastel-1-dark',
    Icon: AlertCircle,
  },
  withdrawn: {
    label: 'Consent withdrawn',
    variant: 'neutral',
    className: 'bg-pastel-1-light text-pastel-1-dark',
    Icon: XCircle,
  },
  refused: {
    label: 'Consent refused',
    variant: 'neutral',
    className: 'bg-pastel-1-light text-pastel-1-dark',
    Icon: XCircle,
  },
  // Product: blocked has no badge of its own — it is the 3rd refusal, so it
  // keeps showing Refused; request/remind are simply unavailable.
  blocked: {
    label: 'Consent refused',
    variant: 'neutral',
    className: 'bg-pastel-1-light text-pastel-1-dark',
    Icon: XCircle,
  },
  // Product: not_required displays as plain "Consented"
  not_required: {
    label: 'Consent granted',
    variant: 'neutral',
    className: 'bg-pastel-2-light text-pastel-2-dark',
    Icon: CheckCircle,
  },
  no_expiry: {
    label: 'Consent granted',
    variant: 'neutral',
    className: 'bg-pastel-2-light text-pastel-2-dark',
    Icon: ShieldTick,
  },
};

// Overrides the status tag when the candidate is anonymized (terminal state —
// no consent actions are available).
export const ANONYMIZED_TAG: ConsentTagConfig = {
  label: 'Anonymized',
  variant: 'neutral',
  className: 'bg-secondary-50 text-secondary-700',
  Icon: UserQuestionmark,
};

export interface ConsentActionConfig {
  label: string;
  Icon: IconComponent;
  destructive?: boolean;
}

export const CONSENT_ACTION_CONFIG: Record<
  ConsentActionKey,
  ConsentActionConfig
> = {
  request: { label: 'Request consent', Icon: Send01 },
  remind: { label: 'Send reminder', Icon: Mail01 },
  mark_manually: { label: 'Mark consent manually', Icon: Pencil01 },
  anonymize: {
    label: 'Anonymize candidate',
    Icon: UserQuestionmark,
    destructive: true,
  },
};

// Ordered menu actions per status (product mapping, 2026-07). `anonymize`
// entries render only when the user has the anonymize permission.
export const CONSENT_STATUS_ACTIONS: Record<
  DerivedConsentStatus,
  ConsentActionKey[]
> = {
  not_collected: ['request', 'mark_manually', 'anonymize'],
  requested: ['remind', 'mark_manually', 'anonymize'],
  granted: ['mark_manually'],
  expiring_soon: ['request', 'mark_manually'],
  expired: ['mark_manually', 'anonymize'],
  withdrawn: ['mark_manually', 'anonymize'],
  refused: ['request', 'anonymize'],
  blocked: ['anonymize'],
  not_required: ['mark_manually'],
  no_expiry: ['mark_manually'],
};

// Statuses already in the granted family — MarkConsentModal skips
// mark_granted for these and goes straight to extend.
export const GRANTED_FAMILY: DerivedConsentStatus[] = [
  'granted',
  'expiring_soon',
  'no_expiry',
  'not_required',
];

// Single source of truth for consent error copy (BE contract §13).
const CONSENT_ERRORS = consentErrors as Record<string, { message: string }>;

const FALLBACK_ERROR = 'Something went wrong. Please try again.';

// Serialize an error from any shape we produce: axios `response.data`, the
// asserted ATS envelope (`responseData` / `message`), or a plain reject value.
const errorRaw = (error: unknown): string => {
  const e = error as {
    response?: { data?: unknown };
    responseData?: unknown;
    message?: string;
  };
  return JSON.stringify(
    e?.response?.data ?? e?.responseData ?? e?.message ?? error ?? ''
  );
};

// BE errors arrive either bare (["err_x"]) or wrapped ("Validation failed:
// Mode err_x") — substring-match, never equality-match.
export const consentErrorMessage = (
  error: unknown,
  fallback: string = FALLBACK_ERROR
): string => {
  const raw = errorRaw(error);
  const code = Object.keys(CONSENT_ERRORS).find((k) => raw.includes(k));
  return code ? CONSENT_ERRORS[code].message : fallback;
};

// True when the anonymize call failed because the candidate is still active —
// callers re-submit with confirm:true. The BE signals this either via the
// error code or a `has_active_applications: true` flag on the 422 body.
export const isActiveApplicationsError = (error: unknown): boolean => {
  const raw = errorRaw(error);
  return (
    raw.includes('err_confirm_required_active_applications') ||
    raw.includes('"has_active_applications":true')
  );
};
